import React, { useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage, injectIntl, intlShape } from '../../util/reactIntl';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { StaticPage } from '../../containers';
import {
  CTAButton,
  Footer,
  LayoutSingleColumn,
  LayoutWrapperFooter,
  LayoutWrapperMain,
  LayoutWrapperTopbar,
  NamedLink,
} from '../../components';
import { cityContent } from '../../data/cityLandingContent';
import LandingHeader from '../../components/LandingHeader/LandingHeader';
import { defaultMarketplaceSearchParams } from '../../util/defaultSearchParams';
import { stringify } from '../../util/urlHelpers';
import { fetchCityListingCounts } from './LandingPageCities.duck';
import heroImage from '../../assets/landingRefresh/cities-hero-storefront.jpg';

import css from './LandingPageCities.module.css';

export const formatCityName = cityNameKey => {
  return cityNameKey.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase());
};

const LandingPageCitiesComponent = props => {
  const {
    intl,
    cityListingCounts,
    cityListingCountsLoaded,
    fetchCityListingCountsInProgress,
    onFetchCityListingCounts,
  } = props;
  const title = intl.formatMessage({ id: 'LandingPageCities.title' });
  const description = intl.formatMessage({ id: 'LandingPageCities.description' });
  const cityEntries = useMemo(() => {
    return Object.entries(cityContent)
      .map(([key, cityData], index) => ({ key, cityData, index }))
      .sort((a, b) => {
        const countA = Number.isFinite(cityListingCounts[a.key])
          ? cityListingCounts[a.key]
          : -1;
        const countB = Number.isFinite(cityListingCounts[b.key])
          ? cityListingCounts[b.key]
          : -1;

        return countB - countA || a.index - b.index;
      });
  }, [cityListingCounts]);
  const defaultSearchLink = { search: `?${stringify(defaultMarketplaceSearchParams)}` };

  useEffect(() => {
    if (!cityListingCountsLoaded && !fetchCityListingCountsInProgress) {
      onFetchCityListingCounts();
    }
  }, [cityListingCountsLoaded, fetchCityListingCountsInProgress, onFetchCityListingCounts]);

  return (
    <StaticPage
      title={title}
      description={description}
      schema={{
        '@context': 'http://schema.org',
        '@type': 'WebPage',
        name: title,
        description,
      }}
    >
      <LayoutSingleColumn>
        <LayoutWrapperTopbar>
          <LandingHeader currentPage="LandingPageCities" />
        </LayoutWrapperTopbar>

        <LayoutWrapperMain className={css.mainWrapper}>
          <section
            className={css.hero}
            style={{
              backgroundImage: `url(${heroImage})`,
            }}
          >
            <div className={css.heroInner}>
              <h1 className={css.mainTitle}>
                <FormattedMessage id="LandingPageCities.mainTitle" />
              </h1>
              <p className={css.mainDescription}>
                <FormattedMessage id="LandingPageCities.mainDescription" />
              </p>
              <div className={css.heroActions}>
                <CTAButton
                  name="SearchPage"
                  to={defaultSearchLink}
                  variant="secondary"
                  size="large"
                  className={css.heroSecondaryButton}
                >
                  <FormattedMessage id="SectionHeroUuper.browseButton" />
                </CTAButton>
              </div>
            </div>
          </section>

          <section className={css.gridSection}>
            <div className={css.cityGrid}>
              {cityEntries.map(({ key, cityData }) => {
                const cityLocalized = intl.formatMessage({ id: `LandingPageCities.${key}` });
                const city = cityLocalized === `LandingPageCities.${key}` ? key : cityLocalized;

                return (
                  <NamedLink key={key} name={cityData.routeName} className={css.cityCard}>
                    <div
                      className={css.cityImage}
                      style={{
                        backgroundImage: `linear-gradient(180deg, rgba(10, 16, 14, 0.12) 0%, rgba(10, 16, 14, 0.58) 100%), url(${cityData.image})`,
                      }}
                    />
                    <div className={css.cityContent}>
                      <div className={css.cityName}>{formatCityName(city)}</div>
                      <div className={css.cityMeta}>
                        <FormattedMessage id="LandingFooter.forBrands" />
                      </div>
                    </div>
                  </NamedLink>
                );
              })}
            </div>
          </section>

          <section className={css.bottomCtaSection}>
            <div className={css.bottomCtaInner}>
              <CTAButton
                name="SearchPage"
                to={defaultSearchLink}
                size="compact"
                className={css.heroPrimaryButton}
              >
                <FormattedMessage id="LandingUuper.searchButton" />
              </CTAButton>
            </div>
          </section>
        </LayoutWrapperMain>

        <LayoutWrapperFooter>
          <Footer />
        </LayoutWrapperFooter>
      </LayoutSingleColumn>
    </StaticPage>
  );
};

LandingPageCitiesComponent.propTypes = {
  intl: intlShape.isRequired,
  cityListingCounts: PropTypes.object,
  cityListingCountsLoaded: PropTypes.bool.isRequired,
  fetchCityListingCountsInProgress: PropTypes.bool.isRequired,
  onFetchCityListingCounts: PropTypes.func.isRequired,
};

LandingPageCitiesComponent.defaultProps = {
  cityListingCounts: {},
};

const mapStateToProps = state => {
  const {
    cityListingCounts,
    cityListingCountsLoaded,
    fetchCityListingCountsInProgress,
  } = state.LandingPageCities || {};

  return {
    cityListingCounts,
    cityListingCountsLoaded: !!cityListingCountsLoaded,
    fetchCityListingCountsInProgress: !!fetchCityListingCountsInProgress,
  };
};

const mapDispatchToProps = dispatch => ({
  onFetchCityListingCounts: () => dispatch(fetchCityListingCounts()),
});

const LandingPageCities = compose(
  connect(mapStateToProps, mapDispatchToProps),
  injectIntl
)(LandingPageCitiesComponent);

export default LandingPageCities;
